/**
 * The build's claim-volume warning, on screen (review #374, 2026-10-09).
 *
 * The builder flags every person-month billing far above the person's job title that
 * month (`claim_volume_outliers` in scripts/build_bundle.py, MEASURE-RULES §12.7), with
 * the claims' top source worklist, and ships them as `claimVolumeOutliers`, cut to the
 * caller by the API. This reads that: the rule lives in the builder, once, so the run
 * log, the Data & method page and the marks here always agree. Nothing here decides
 * what is an outlier.
 *
 * Billed claims reach a person through attribution as well as through work: the rep who
 * worked the claim, else the claim matcher's predicted user. When the matcher points a
 * whole list at one person, they bill thousands of claims they never touched. The marks
 * only say so. They change no figure, score or peer average, by decision (2026-10-09,
 * MEASURE-RULES §12.7): a flagged month is probably wrong but is taken as correct until
 * the source data is fixed, so it stays in the peer averages.
 */
import type { ClaimVolumeOutliers } from '@/data/types'
import { int, pct } from '@/format'

export interface VolumeMonth {
  month: string
  claims: number
  /** The job title's median that month, over people with at least one claim. */
  titleMedian: number
  /** How many people that median is over. */
  peers: number
  jobTitle: string
  /** The person's top source worklist over their flagged months, and its share. */
  worklist: string | null
  worklistShare: number | null
}

export interface VolumeFlags {
  /** Person → flagged months by 'YYYY-MM'. People with none are absent. */
  byPerson: Map<string, Map<string, VolumeMonth>>
  /** The builder's rule in words; null on a bundle without the check. */
  rule: string | null
}

export const NO_VOLUME_FLAGS: VolumeFlags = { byPerson: new Map(), rule: null }

/** The bundle's warning as marks to look up. Empty on a bundle built before it. */
export function volumeFlags(o: ClaimVolumeOutliers | undefined): VolumeFlags {
  if (!o) return NO_VOLUME_FLAGS
  const byPerson = new Map<string, Map<string, VolumeMonth>>()
  for (const r of o.rows) {
    const months = new Map<string, VolumeMonth>()
    for (const m of r.months)
      months.set(m.month, {
        ...m,
        jobTitle: r.jobTitle,
        worklist: r.worklist,
        worklistShare: r.worklistShare,
      })
    if (months.size) byPerson.set(r.employee, months)
  }
  return {
    byPerson,
    rule: `more than ${o.factor}× the job title’s median that month, and at least ${int(o.margin)} over it`,
  }
}

/** What a flagged month says on hover. */
export function volumeNote(m: VolumeMonth): string {
  const times = m.titleMedian > 0 ? m.claims / m.titleMedian : null
  const x = times === null ? '' : `${times < 10 ? times.toFixed(1) : Math.round(times)}×`
  const source = m.worklist
    ? ` Most came through “${m.worklist}”${m.worklistShare !== null ? ` (${pct(m.worklistShare)} of the flagged months)` : ''}, the claim matcher’s predicted worklist.`
    : ''
  return (
    `${int(m.claims)} billed claims: ${x ? `${x} ` : ''}the ${int(Math.round(m.titleMedian))} median of the ` +
    `${int(m.peers)} ${m.jobTitle} holders billing that month.${source} Volume like this usually comes from ` +
    `claims assigned to the person automatically, not from work they did. Read this month with care before comparing it.`
  )
}
